import { supabase } from '@/utils/supabase';
import {
  collapseBorrowerDeliveryRotations,
  normalizeAppraisalDeliveryRow,
  sortAppraisalDeliveryRows,
  type AppraisalDeliveryAttachmentContext,
  type AppraisalDeliveryLinkContext,
  type AppraisalDeliveryLoanContext,
  type AppraisalDeliveryPipeline,
  type AppraisalDeliveryQueueRecord,
  type AppraisalDeliveryReportRow
} from '@/components/reports/appraisalDeliveryModel';

export interface AppraisalDeliveryReportQuery {
  from?: Date;
  to?: Date;
}

export interface AppraisalDeliveryReportResult {
  rows: AppraisalDeliveryReportRow[];
  /** Pipelines that hit the per-pipeline read cap. The UI must say so. */
  truncatedPipelines: AppraisalDeliveryPipeline[];
}

interface RawQueueRow {
  id: string;
  encompass_loan_id: string;
  attachment_id: string;
  status: string;
  skip_reason?: string | null;
  validation_details?: Record<string, unknown> | null;
  scheduled_for?: string | null;
  created_at: string;
  updated_at?: string | null;
  decided_at?: string | null;
  email_sent_at?: string | null;
  borrower_emails?: string[] | null;
  cc_emails?: string[] | null;
  resend_id?: string | null;
  receipt_retry_count?: number | null;
  receipt_next_retry_at?: string | null;
  receipt_last_error?: string | null;
  delivery_receipt_attachment_id?: string | null;
  extraction_retry_count?: number | null;
  field_3858_written_at?: string | null;
  field_3858_retry_count?: number | null;
  field_3858_gave_up_at?: string | null;
  exception_notified_at?: string | null;
}

interface RawLoanRow {
  id: string;
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_folder: string | null;
  milestone: string | null;
}

interface RawAttachmentRow {
  encompass_loan_id: string;
  attachment_id: string;
  title: string | null;
  uploaded_date: string | null;
  is_generated_copy: boolean | null;
  generated_copy_type: string | null;
  file_size: number | null;
}

interface RawLinkRow {
  id: string;
  queue_row_id: string;
  expires_at: string;
  download_count: number;
  first_downloaded_at: string | null;
  last_downloaded_at: string | null;
  revoked_at: string | null;
}

const MAX_ROWS_PER_PIPELINE = 2_000;
const ENRICHMENT_CHUNK_SIZE = 100;

/**
 * Per-table column lists. These are NOT interchangeable: `updated_at` exists
 * only on the CDA-order and AVM tables, and the CDA-order table has no
 * recipient columns at all. Selecting a column a table does not have fails the
 * whole request, and pretending one exists (the first build fell back to
 * email_sent_at || created_at) puts a queued timestamp under a "Last updated"
 * heading.
 */
type DecisionColumn = 'decided_at' | 'updated_at';

const PIPELINE_SOURCES: Record<
  AppraisalDeliveryPipeline,
  { table: string; columns: string; decisionColumn: DecisionColumn }
> = {
  cda_order: {
    table: 'appraisal_cda_order_notifications',
    decisionColumn: 'updated_at',
    columns: [
      'id', 'encompass_loan_id', 'attachment_id', 'status', 'skip_reason',
      'validation_details', 'scheduled_for', 'created_at', 'updated_at',
      'email_sent_at', 'resend_id', 'extraction_retry_count', 'exception_notified_at'
    ].join(', ')
  },
  cda_delivery: {
    table: 'cda_delivery_notifications',
    decisionColumn: 'decided_at',
    columns: [
      'id', 'encompass_loan_id', 'attachment_id', 'status', 'skip_reason',
      'validation_details', 'scheduled_for', 'created_at', 'decided_at', 'email_sent_at',
      'borrower_emails', 'cc_emails', 'resend_id', 'receipt_retry_count',
      'receipt_next_retry_at', 'receipt_last_error', 'delivery_receipt_attachment_id',
      'extraction_retry_count', 'field_3858_written_at', 'field_3858_retry_count',
      'field_3858_gave_up_at', 'exception_notified_at'
    ].join(', ')
  },
  transferred: {
    table: 'transferred_appraisal_notifications',
    decisionColumn: 'decided_at',
    columns: [
      'id', 'encompass_loan_id', 'attachment_id', 'status', 'skip_reason',
      'validation_details', 'scheduled_for', 'created_at', 'decided_at', 'email_sent_at',
      'borrower_emails', 'cc_emails', 'resend_id', 'receipt_retry_count',
      'receipt_next_retry_at', 'receipt_last_error', 'delivery_receipt_attachment_id',
      'extraction_retry_count', 'exception_notified_at'
    ].join(', ')
  },
  avm: {
    table: 'avm_delivery_notifications',
    decisionColumn: 'updated_at',
    columns: [
      'id', 'encompass_loan_id', 'attachment_id', 'status', 'skip_reason',
      'validation_details', 'scheduled_for', 'created_at', 'updated_at',
      'email_sent_at', 'borrower_emails', 'cc_emails', 'resend_id',
      'receipt_retry_count', 'receipt_next_retry_at', 'receipt_last_error',
      'delivery_receipt_attachment_id', 'extraction_retry_count',
      'field_3858_written_at', 'field_3858_retry_count', 'field_3858_gave_up_at',
      'exception_notified_at'
    ].join(', ')
  }
};

/**
 * PostgREST form of:
 *   COALESCE(email_sent_at, decision_column, created_at) BETWEEN from AND to
 *
 * Each branch guards the columns to its left with IS NULL, so one row can only
 * be classified by its first available completion timestamp. This avoids the
 * old nullable-column bug where every unsent row disappeared.
 */
export function completedRangeFilter(
  decisionColumn: DecisionColumn,
  from: Date,
  to: Date
): string {
  const fromIso = from.toISOString();
  const toIso = to.toISOString();
  return [
    `and(email_sent_at.gte.${fromIso},email_sent_at.lte.${toIso})`,
    `and(email_sent_at.is.null,${decisionColumn}.gte.${fromIso},${decisionColumn}.lte.${toIso})`,
    `and(email_sent_at.is.null,${decisionColumn}.is.null,created_at.gte.${fromIso},created_at.lte.${toIso})`
  ].join(',');
}

function defaultRange(): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { from, to };
}

async function loadQueue(
  pipeline: AppraisalDeliveryPipeline,
  from: Date,
  to: Date
): Promise<{ records: AppraisalDeliveryQueueRecord[]; truncated: boolean }> {
  const source = PIPELINE_SOURCES[pipeline];
  const { data, error } = await supabase
    .from(source.table)
    .select(source.columns)
    .or(completedRangeFilter(source.decisionColumn, from, to))
    .order('created_at', { ascending: false })
    .limit(MAX_ROWS_PER_PIPELINE);

  if (error) {
    throw new Error(`Unable to load ${pipeline} appraisal delivery rows: ${error.message}`);
  }

  const raw = (data ?? []) as unknown as RawQueueRow[];
  return {
    truncated: raw.length >= MAX_ROWS_PER_PIPELINE,
    records: raw.map(row => ({
      id: row.id,
      pipeline,
      encompassLoanId: row.encompass_loan_id,
      attachmentId: row.attachment_id,
      status: row.status,
      skipReason: row.skip_reason || null,
      validationDetails: row.validation_details || null,
      scheduledFor: row.scheduled_for || null,
      createdAt: row.created_at,
      // Null when the table has no updated_at. Never substitute another column.
      updatedAt: row.updated_at ?? null,
      decidedAt: row.decided_at ?? null,
      emailSentAt: row.email_sent_at || null,
      borrowerEmails: Array.isArray(row.borrower_emails) ? row.borrower_emails : [],
      ccEmails: Array.isArray(row.cc_emails) ? row.cc_emails : [],
      resendId: row.resend_id || null,
      receiptRetryCount: row.receipt_retry_count || 0,
      receiptNextRetryAt: row.receipt_next_retry_at || null,
      receiptLastError: row.receipt_last_error || null,
      deliveryReceiptAttachmentId: row.delivery_receipt_attachment_id || null,
      extractionRetryCount: row.extraction_retry_count || 0,
      field3858WrittenAt: row.field_3858_written_at || null,
      field3858RetryCount: row.field_3858_retry_count ?? null,
      field3858GaveUpAt: row.field_3858_gave_up_at || null,
      exceptionNotifiedAt: row.exception_notified_at || null
    }))
  };
}

function chunk<T>(values: T[]): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < values.length; index += ENRICHMENT_CHUNK_SIZE) {
    result.push(values.slice(index, index + ENRICHMENT_CHUNK_SIZE));
  }
  return result;
}

async function loadLoans(
  rows: AppraisalDeliveryQueueRecord[]
): Promise<Map<string, AppraisalDeliveryLoanContext>> {
  const ids = [...new Set(rows.map(row => row.encompassLoanId).filter(Boolean))];
  const result = new Map<string, AppraisalDeliveryLoanContext>();
  for (const idsChunk of chunk(ids)) {
    const { data, error } = await supabase
      .from('encompass_loans')
      .select('id, encompass_loan_id, trans_details_loan_number, borrower_first_name, borrower_last_name, loan_folder, milestone')
      .in('encompass_loan_id', idsChunk);
    if (error) {
      throw new Error(`Unable to load appraisal delivery loans: ${error.message}`);
    }
    for (const loan of (data ?? []) as unknown as RawLoanRow[]) {
      result.set(loan.encompass_loan_id, {
        id: loan.id,
        loanNumber: loan.trans_details_loan_number || loan.encompass_loan_id,
        borrowerName: [loan.borrower_first_name, loan.borrower_last_name].filter(Boolean).join(' ') || 'Unknown borrower',
        loanFolder: loan.loan_folder,
        milestone: loan.milestone
      });
    }
  }
  return result;
}

function canonicalAttachmentId(row: AppraisalDeliveryQueueRecord): string {
  const details = row.validationDetails;
  const candidates = [
    details?.canonicalAttachmentId,
    details?.canonical_attachment_id,
    row.attachmentId
  ];
  return candidates.find((value): value is string => typeof value === 'string' && value.length > 0)
    || row.attachmentId;
}

function deliveredAttachmentId(row: AppraisalDeliveryQueueRecord): string {
  const details = row.validationDetails;
  const candidates = [
    details?.deliveryAttachmentId,
    details?.delivery_attachment_id,
    row.attachmentId
  ];
  return candidates.find((value): value is string => typeof value === 'string' && value.length > 0)
    || row.attachmentId;
}

function attachmentMapKey(encompassLoanId: string, attachmentId: string): string {
  return `${encompassLoanId}:${attachmentId}`;
}

async function loadAttachments(
  rows: AppraisalDeliveryQueueRecord[]
): Promise<Map<string, AppraisalDeliveryAttachmentContext>> {
  const attachmentIds = [...new Set(rows.flatMap(row => [
    row.attachmentId,
    canonicalAttachmentId(row),
    deliveredAttachmentId(row)
  ]))];
  const result = new Map<string, AppraisalDeliveryAttachmentContext>();
  for (const idsChunk of chunk(attachmentIds)) {
    const { data, error } = await supabase
      .from('encompass_attachments')
      .select('encompass_loan_id, attachment_id, title, uploaded_date, is_generated_copy, generated_copy_type, file_size')
      .in('attachment_id', idsChunk);
    if (error) {
      throw new Error(`Unable to load appraisal delivery documents: ${error.message}`);
    }
    for (const attachment of (data ?? []) as unknown as RawAttachmentRow[]) {
      result.set(attachmentMapKey(attachment.encompass_loan_id, attachment.attachment_id), {
        attachmentId: attachment.attachment_id,
        title: attachment.title,
        uploadedDate: attachment.uploaded_date,
        isGeneratedCopy: attachment.is_generated_copy === true,
        generatedCopyType: attachment.generated_copy_type,
        fileSize: attachment.file_size
      });
    }
  }
  return result;
}

async function loadLinks(
  rows: AppraisalDeliveryQueueRecord[]
): Promise<Map<string, AppraisalDeliveryLinkContext>> {
  const rowIds = rows.filter(row => row.pipeline !== 'cda_order').map(row => row.id);
  const result = new Map<string, AppraisalDeliveryLinkContext>();
  for (const idsChunk of chunk(rowIds)) {
    const { data, error } = await supabase
      .from('appraisal_delivery_links')
      .select('id, queue_row_id, expires_at, download_count, first_downloaded_at, last_downloaded_at, revoked_at')
      .in('queue_row_id', idsChunk);
    if (error) {
      throw new Error(`Unable to load appraisal delivery links: ${error.message}`);
    }
    for (const link of (data ?? []) as unknown as RawLinkRow[]) {
      result.set(link.queue_row_id, {
        id: link.id,
        expiresAt: link.expires_at,
        downloadCount: link.download_count,
        firstDownloadedAt: link.first_downloaded_at,
        lastDownloadedAt: link.last_downloaded_at,
        revokedAt: link.revoked_at
      });
    }
  }
  return result;
}

const PIPELINES: AppraisalDeliveryPipeline[] = ['cda_order', 'cda_delivery', 'transferred', 'avm'];

export async function getAppraisalDeliveryReport(
  query: AppraisalDeliveryReportQuery = {}
): Promise<AppraisalDeliveryReportResult> {
  const range = defaultRange();
  const from = query.from || range.from;
  const to = query.to || range.to;

  const loaded = await Promise.all(
    PIPELINES.map(pipeline => loadQueue(pipeline, from, to))
  );
  const queues = loaded.flatMap(result => result.records);
  const truncatedPipelines = PIPELINES.filter((_, index) => loaded[index].truncated);

  const [loans, attachments, links] = await Promise.all([
    loadLoans(queues),
    loadAttachments(queues),
    loadLinks(queues)
  ]);

  const now = new Date();
  const rows = collapseBorrowerDeliveryRotations(queues.map(queue => {
    const canonicalId = canonicalAttachmentId(queue);
    const deliveredId = deliveredAttachmentId(queue);
    const queuedAttachment = attachments.get(attachmentMapKey(queue.encompassLoanId, queue.attachmentId));
    const canonicalAttachment = attachments.get(attachmentMapKey(queue.encompassLoanId, canonicalId));
    const deliveredAttachment = attachments.get(attachmentMapKey(queue.encompassLoanId, deliveredId));
    return normalizeAppraisalDeliveryRow({
      queue,
      loan: loans.get(queue.encompassLoanId) || null,
      attachment: deliveredAttachment || queuedAttachment || canonicalAttachment || null,
      link: links.get(queue.id) || null
    }, now);
  })).sort(sortAppraisalDeliveryRows);

  return { rows, truncatedPipelines };
}
